import { Alert } from '@/models/Alert'
import { VehicleAmenityAlert } from '@/models/dto/Vehicle'
import { VehicleAmenityAlertType } from '@/utils/enum'
import { getAmenityAlertLabelFromKey } from '@/utils/amenity'
import {
  loadFromSession,
  removeFromSession,
  saveToSession,
} from '@/utils/sessionStorage'

const VEHICLE_AMENITY_ALERTS_KEY = 'vehicleAmenityAlerts'

type AmenityAlertCopy = {
  color: string
  singular: (vehicleName: string, amenityLabel: string) => {
    title: string
    text: string
  }
  plural: (count: number) => {
    title: string
    text: string
  }
}

export const AMENITY_ALERT_COPY: Record<VehicleAmenityAlertType, AmenityAlertCopy> = {
  [VehicleAmenityAlertType.UnderReview]: {
    color: 'error',
    singular: (vehicleName) => ({
      title: `Vehicle ${vehicleName} needs attention`,
      text: 'Vehicle needs your attention due to amenity issues. Go to the Vehicles page to resolve.',
    }),
    plural: (count) => ({
      title: `${count} vehicle amenities need attention`,
      text: 'Vehicle(s) need your attention due to amenity issues. Go to the Vehicles page to resolve.',
    }),
  },
  [VehicleAmenityAlertType.ReviewApproved]: {
    color: 'success',
    singular: (vehicleName, amenityLabel) => ({
      title: `Vehicle ${vehicleName} verified`,
      text: `${amenityLabel} restored. Vehicle eligible.`,
    }),
    plural: (count) => ({
      title: `${count} vehicle amenities verified`,
      text: 'Amenities restored. Vehicle(s) eligible.',
    }),
  },
  [VehicleAmenityAlertType.RequiresAdditionalProof]: {
    color: 'error',
    singular: (vehicleName, amenityLabel) => ({
      title: 'Verification rejected',
      text: `Insufficient evidence for ${amenityLabel} on ${vehicleName}. Please review vehicle profile.`,
    }),
    plural: (count) => ({
      title: `${count} verifications rejected`,
      text: 'Insufficient evidence provided. Please review vehicle profile(s).',
    }),
  },
  [VehicleAmenityAlertType.ReviewRejected]: {
    color: 'black',
    singular: (vehicleName, amenityLabel) => ({
      title: 'Verification rejected',
      text: `${amenityLabel} marked "Not Equipped" on ${vehicleName}. Vehicle updated.`,
    }),
    plural: (count) => ({
      title: `${count} verifications rejected`,
      text: 'Amenities marked "Not Equipped". Vehicle(s) updated.',
    }),
  },
}

export function saveVehicleAmenityAlerts(
  alerts: VehicleAmenityAlert[]
): void {
  saveToSession(VEHICLE_AMENITY_ALERTS_KEY, alerts)
}

export function loadVehicleAmenityAlerts(): VehicleAmenityAlert[] {
  return loadFromSession<VehicleAmenityAlert[]>(VEHICLE_AMENITY_ALERTS_KEY) ?? []
}

export function clearVehicleAmenityAlerts(): void {
  removeFromSession(VEHICLE_AMENITY_ALERTS_KEY)
}

/**
 * Appends newly fetched alerts to the existing pending queue.
 */
export function mergeAmenityAlerts(
  pending: VehicleAmenityAlert[],
  fetched: VehicleAmenityAlert[]
): VehicleAmenityAlert[] {
  return [...pending, ...fetched]
}

export function buildAmenityAlert(
  alertType: VehicleAmenityAlertType,
  alerts: VehicleAmenityAlert[]
): Alert | null {
  if (!alerts.length) {
    return null
  }

  const copy = AMENITY_ALERT_COPY[alertType]
  if (!copy) {
    return null
  }

  const message =
    alerts.length === 1
      ? copy.singular(
          alerts[0].vehicleName,
          getAmenityAlertLabelFromKey(alerts[0].amenityKey)
        )
      : copy.plural(alerts.length)

  return {
    ...message,
    color: copy.color,
  }
}
